import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { shopApi } from '../lib/api';
import { Package, Truck, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';

const MyOrdersPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    shopApi.getMyOrders().then(d => { setOrders(d.orders || []); setLoading(false); }).catch(() => setLoading(false));
  }, [isAuthenticated, navigate]);

  if (loading) return <div className="min-h-screen bg-[#030014]"><Navbar /><div className="flex items-center justify-center h-96"><div className="spinner" /></div></div>;

  return (
    <div className="min-h-screen bg-[#030014]" data-testid="my-orders-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 pt-28 pb-16">
        <h1 className="text-2xl font-bold mb-6">My Orders</h1>
        {orders.length === 0 ? (
          <div className="text-center py-16 admin-card">
            <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No orders yet</p>
            <Link to="/browse" className="text-[#8B5CF6] text-sm mt-2 inline-block">Continue browsing</Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order.order_id} className="admin-card overflow-hidden" data-testid={`order-${order.order_id}`}>
                <button onClick={() => setExpanded(expanded === order.order_id ? null : order.order_id)} className="w-full p-4 flex items-center justify-between text-left">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#8B5CF6]/10 flex items-center justify-center">
                      <Package className="w-5 h-5 text-[#8B5CF6]" />
                    </div>
                    <div>
                      <p className="font-semibold">Order #{order.order_number}</p>
                      <p className="text-xs text-gray-400">{new Date(order.created_at).toLocaleDateString('en-US')} — {order.creator_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${order.status === 'fulfilled' ? 'bg-[#00FF9D]/10 text-[#00FF9D]' : 'bg-yellow-500/10 text-yellow-400'}`}>
                      {order.status === 'fulfilled' ? 'Shipped' : 'Processing'}
                    </span>
                    <span className="font-semibold">${order.amount?.toFixed(2)}</span>
                    {expanded === order.order_id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </button>

                {expanded === order.order_id && (
                  <div className="border-t border-white/10 p-4 space-y-4">
                    {/* Items */}
                    <div className="space-y-2">
                      {(order.items || []).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-2 bg-white/[0.03] rounded-lg">
                          {item.image && <img src={item.image} alt="" className="w-12 h-12 rounded object-cover" />}
                          <div className="flex-1">
                            <p className="text-sm font-medium">{item.product_name}</p>
                            {Object.keys(item.selected_variants || {}).length > 0 && (
                              <p className="text-xs text-[#8B5CF6]">{Object.entries(item.selected_variants).map(([k,v]) => `${k}: ${v}`).join(' / ')}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm">×{item.quantity}</p>
                            <p className="text-sm font-semibold">${item.line_total?.toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Tracking */}
                    {order.tracking_code && (
                      <div className="flex items-center gap-3 p-3 bg-[#8B5CF6]/5 rounded-lg border border-[#8B5CF6]/20">
                        <Truck className="w-5 h-5 text-[#8B5CF6]" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-400">Tracking Code</p>
                          <p className="font-mono font-medium">{order.tracking_code}</p>
                        </div>
                        <Link to={`/track?code=${encodeURIComponent(order.tracking_code)}`} className="flex items-center gap-1 text-[#8B5CF6] text-sm hover:underline" data-testid={`track-btn-${order.order_id}`}>
                          Track <ExternalLink className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    )}

                    {/* Shipping */}
                    {order.shipping && (
                      <div className="text-xs text-gray-400 p-3 bg-white/[0.02] rounded-lg">
                        <p className="text-gray-300 font-medium mb-1">Shipping to:</p>
                        <p>{order.shipping.full_name}</p>
                        <p>{order.shipping.address}{order.shipping.address_line2 ? `, ${order.shipping.address_line2}` : ''}</p>
                        <p>{order.shipping.postal_code} {order.shipping.city}, {order.shipping.country}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrdersPage;
